import { useCallback, useEffect, useState } from "react";

import { toast } from "sonner";

import { useGlobalContext } from "../Context/GlobalContext";

export const useRating = (taskId, enabled) => {
  const { packkyApi } = useGlobalContext();

  const [driverRating, setDriverRating] = useState("");

  const [customerMessage, setCustomerMessage] = useState("");

  const [ratingSubmitting, setRatingSubmitting] = useState(false);

  const [ratingSubmitted, setRatingSubmitted] = useState(false);

  const [existingRating, setExistingRating] = useState(null);

  const [loadingRating, setLoadingRating] = useState(false);

  const [ratingDismissed, setRatingDismissed] = useState(false);

  // =====================================================
  // GET EXISTING DRIVER RATING
  // ONLY AFTER DELIVERY
  // =====================================================

  useEffect(() => {
    if (!taskId || !enabled) {
      return;
    }

    let cancelled = false;

    const fetchRating = async () => {
      try {
        setLoadingRating(true);

        const payload = {
          taskId: String(taskId),
        };

        const res = await packkyApi.post(
          "/api/DriverTracker/v1.0/getDriverTaskRating",
          payload,
        );

        if (!cancelled && res?.data?.responseCode === "00" && res?.data?.data) {
          setExistingRating(res.data.data);

          setRatingSubmitted(true);
        }
      } catch (error) {
        console.error("GET DRIVER RATING ERROR:", error);
      } finally {
        if (!cancelled) {
          setLoadingRating(false);
        }
      }
    };

    fetchRating();

    return () => {
      cancelled = true;
    };
  }, [taskId, enabled, packkyApi]);

  // =====================================================
  // SUBMIT DELIVERY RATING
  // =====================================================

  const submitDeliveryRating = useCallback(
    async ({ orderDetails }) => {
      if (!taskId) {
        toast.error("Task ID not available");

        return null;
      }

      if (!driverRating) {
        toast.error("Please select rating");

        return null;
      }

      const payload = {
        taskId: String(taskId),

        customerName: orderDetails?.customer_name || "",

        customerMobile: orderDetails?.customer_mobile || "",

        driverRating: String(driverRating),

        customerMessege: customerMessage || "",
      };

      try {
        setRatingSubmitting(true);

        const res = await packkyApi.post(
          "/api/DriverTracker/v1.0/insertTaskDeliveryRating",
          payload,
        );

        if (res?.data?.responseCode === "00") {
          toast.success("Thank you for your feedback");

          setExistingRating({
            rating: driverRating,

            customer_message: customerMessage,
          });

          setRatingSubmitted(true);

          return res?.data;
        }

        toast.error(res?.data?.responseMessage || "Failed to submit rating");

        return null;
      } catch (error) {
        console.error("SUBMIT DRIVER RATING ERROR:", error);

        toast.error("Something went wrong");

        return null;
      } finally {
        setRatingSubmitting(false);
      }
    },
    [taskId, driverRating, customerMessage, packkyApi],
  );

  // =====================================================
  // DISMISS RATING
  // =====================================================

  const dismissRating = useCallback(() => {
    setRatingDismissed(true);
  }, []);

  // =====================================================
  // RESET WHEN TASK CHANGES
  // =====================================================

  useEffect(() => {
    setDriverRating("");
    setCustomerMessage("");
    setRatingSubmitted(false);
    setExistingRating(null);
    setRatingDismissed(false);
  }, [taskId]);

  // =====================================================
  // RETURN
  // =====================================================

  return {
    driverRating,
    setDriverRating,

    customerMessage,
    setCustomerMessage,

    ratingSubmitting,
    ratingSubmitted,

    existingRating,
    loadingRating,

    submitDeliveryRating,

    ratingDismissed,
    dismissRating,
  };
};
